import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tv, Zap, Square, Facebook, Twitter, ExternalLink, Eye, X, MapPin, Send } from "lucide-react";
import { CutInSlide } from "@/components/CutInSlide";

// Browser-only rehearsal: renders the real CutInSlide on a scaled 1920x1080 stage — never touches the stream.
function RehearsalModal({ cutIn, onClose }) {
  const [now, setNow] = useState(() => new Date());
  const [w, setW] = useState(() => Math.min(window.innerWidth - 64, 1600));
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    const r = () => setW(Math.min(window.innerWidth - 64, 1600));
    window.addEventListener("resize", r);
    return () => { clearInterval(t); window.removeEventListener("resize", r); };
  }, []);
  const scale = w / 1920;
  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-8" data-testid="cutin-rehearsal-modal" onClick={onClose}>
      <div className="mb-3 flex w-full max-w-[1600px] items-center justify-between" style={{ width: w }}>
        <span className="inline-flex items-center gap-2 rounded-sm bg-yellow-400 px-3 py-1 font-oswald text-xs font-black uppercase tracking-widest text-black" data-testid="cutin-rehearsal-badge"><Eye className="h-4 w-4" /> Rehearsal · browser only · not on air</span>
        <button data-testid="cutin-rehearsal-close" onClick={onClose} className="inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-widest text-gray-300 hover:text-white"><X className="h-4 w-4" /> Close</button>
      </div>
      <div className="relative overflow-hidden rounded border border-white/10 bg-black" style={{ width: w, height: w * 9 / 16 }} onClick={(e) => e.stopPropagation()}>
        <div className="absolute left-0 top-0 origin-top-left" style={{ width: 1920, height: 1080, transform: `scale(${scale})` }}>
          <CutInSlide cutIn={cutIn} now={now} />
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="mb-1 block font-oswald text-[11px] uppercase tracking-widest text-gray-400">{children}</label>
);

const Toggle = ({ checked, onChange, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-3 text-left"
  >
    <span className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-[#1f63c9]" : "bg-white/20"}`}>
      <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform"
        style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }} />
    </span>
    <span className="text-sm text-white">{label}</span>
  </button>
);

const STATUS_COLOR = { posted: "text-green-400", failed: "text-red-400", test: "text-yellow-300" };

function PlatformBadge({ icon: Icon, status }) {
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase ${STATUS_COLOR[status] || "text-gray-500"}`}>
      <Icon className="h-3 w-3" /> {status || "—"}
    </span>
  );
}

export function CutInTab() {
  const [cfg, setCfg] = useState({ enabled: true, dwell_s: 60, include_spc: true });
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, h, a] = await Promise.all([
      api.get("/broadcast/cut-in/config"),
      api.get("/broadcast/cut-in/history"),
      api.get("/broadcast/cut-in"),
    ]);
    setCfg(c.data); setHistory(h.data.cut_ins || []); setActive(a.data.active);
  };
  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => api.get("/broadcast/cut-in").then((r) => setActive(r.data.active)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try { const { data } = await api.put("/broadcast/cut-in/config", next); setCfg(data); toast.success("Cut-in settings saved"); }
    catch { toast.error("Save failed"); }
  };

  const [rehearsal, setRehearsal] = useState(null);
  const rehearse = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/broadcast/cut-in/rehearsal");
      const dwellMs = (data.cut_in.dwell_s || 60) * 1000;
      setRehearsal({ ...data.cut_in, dwellMs, endsAt: Date.now() + dwellMs });
    } catch { toast.error("Could not load rehearsal"); } finally { setBusy(false); }
  };

  const fireTest = async () => {
    if (!window.confirm("This puts a TEST Tornado Warning on the LIVE stream (YouTube) for the dwell time. Use 'Rehearse in Browser' to preview safely. Fire on air anyway?")) return;
    setBusy(true);
    try {
      await api.post("/broadcast/cut-in/test");
      toast.success("Test cut-in fired — it will appear on /broadcast within ~8 seconds");
      await load();
    } catch { toast.error("Could not fire test cut-in"); } finally { setBusy(false); }
  };

  const endNow = async () => {
    try { await api.delete("/broadcast/cut-in/active"); toast.success("Cut-in cleared — loop resuming"); await load(); }
    catch { toast.error("Could not clear cut-in"); }
  };

  // --- Town Shoutout (admin-fired now; YouTube-chat-fired later) ---
  const [town, setTown] = useState("");
  const [shout, setShout] = useState("");
  const [townBusy, setTownBusy] = useState(false);
  const [townActive, setTownActive] = useState(null);
  useEffect(() => {
    const poll = () => api.get("/spotlight/active").then((r) => setTownActive(r.data.active)).catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);
  const fireTown = async () => {
    if (!town.trim()) { toast.error("Enter a Georgia town or ZIP"); return; }
    setTownBusy(true);
    try {
      const { data } = await api.post("/spotlight", { query: town.trim(), shoutout: shout.trim() || null });
      toast.success(`${data.spotlight.place} is going on air`);
      setTownActive(data.spotlight);
      setTown(""); setShout("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not find that place");
    } finally { setTownBusy(false); }
  };
  const endTown = async () => {
    try { await api.delete("/spotlight/active"); toast.success("Shoutout cleared"); setTownActive(null); }
    catch { toast.error("Could not clear shoutout"); }
  };

  // --- Auto shoutouts from YouTube live chat ---
  const [chatCfg, setChatCfg] = useState({ enabled: false, commands: ["!wx", "!town", "!shoutout"], include_name: true, tip_enabled: true, tip_text: "", tip_interval_min: 5 });
  const [chatStatus, setChatStatus] = useState({});
  const [cmdText, setCmdText] = useState("!wx, !town, !shoutout");
  const [tipText, setTipText] = useState("");
  const [severeText, setSevereText] = useState("");
  useEffect(() => {
    api.get("/spotlight/chat-config").then((r) => { setChatCfg(r.data); setCmdText((r.data.commands || []).join(", ")); setTipText((r.data.tips || []).join("\n")); setSevereText((r.data.tips_severe || []).join("\n")); }).catch(() => {});
    const poll = () => api.get("/spotlight/chat-status").then((r) => setChatStatus(r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, []);
  const saveChat = async (patch) => {
    setChatCfg((c) => ({ ...c, ...patch }));
    try { const { data } = await api.put("/spotlight/chat-config", patch); setChatCfg(data); setCmdText((data.commands || []).join(", ")); setTipText((data.tips || []).join("\n")); setSevereText((data.tips_severe || []).join("\n")); toast.success("Chat settings saved"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-6" data-testid="cutin-tab">
      {rehearsal && <RehearsalModal cutIn={rehearsal} onClose={() => setRehearsal(null)} />}

      {/* Town Shoutout */}
      <div className="glass rounded-sm p-6" data-testid="spotlight-card">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#ffd60a]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Town Shoutout</div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          Pop a viewer's town on air — a friendly shoutout with their local radar and current conditions. Type the town or ZIP
          you see in the live chat and send it. (Automatic YouTube-chat detection can be wired in later.)
        </p>
        <div className="mt-5 grid gap-4 md:grid-cols-[1fr_1.4fr_auto] md:items-end">
          <div>
            <Label>Town or ZIP (Georgia)</Label>
            <input
              data-testid="spotlight-query-input"
              value={town}
              onChange={(e) => setTown(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fireTown()}
              placeholder="e.g. Savannah or 31401"
              className="w-full rounded bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-500"
            />
          </div>
          <div>
            <Label>Custom shoutout (optional)</Label>
            <input
              data-testid="spotlight-shoutout-input"
              value={shout}
              onChange={(e) => setShout(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fireTown()}
              placeholder="Hi to everyone in …!"
              className="w-full rounded bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-500"
            />
          </div>
          <button
            data-testid="spotlight-fire-btn"
            onClick={fireTown}
            disabled={townBusy}
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#ffd60a] px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-[#0a0f1c] hover:bg-yellow-300 disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> Send to Air
          </button>
        </div>
        <div
          data-testid="spotlight-status"
          className={`mt-5 flex items-center justify-between gap-3 rounded-sm border px-4 py-3 ${townActive ? "border-[#ffd60a] bg-[#ffd60a]/10" : "border-white/10 bg-black/40"}`}
        >
          <div className="flex items-center gap-3">
            <span className={townActive ? "live-dot" : "h-2 w-2 rounded-full bg-gray-600"} />
            <div className="text-sm text-white">
              {townActive
                ? <>ON AIR — <span className="font-bold">{townActive.place}</span> · {townActive.remaining_s}s remaining</>
                : "No shoutout on air."}
            </div>
          </div>
          <button
            data-testid="spotlight-end-btn"
            onClick={endTown}
            disabled={!townActive}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-white hover:border-[#ffd60a] disabled:opacity-40"
          >
            <Square className="h-3.5 w-3.5" /> Pull Off Air
          </button>
        </div>

        {/* Automatic from YouTube chat */}
        <div className="mt-6 border-t border-white/10 pt-5" data-testid="chat-auto-section">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">Automatic from YouTube Chat</div>
              <p className="mt-1 text-sm text-gray-400">
                When a viewer types a command in your live chat (e.g. <span className="font-mono text-gray-300">!wx Macon</span>),
                their town pops on air automatically. Reads your live chat in real time.
              </p>
            </div>
            <Toggle testId="chat-auto-toggle" checked={!!chatCfg.enabled} onChange={(v) => saveChat({ enabled: v })} label={chatCfg.enabled ? "On" : "Off"} />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-[1.4fr_auto] md:items-end">
            <div>
              <Label>Trigger commands (comma-separated)</Label>
              <input
                data-testid="chat-commands-input"
                value={cmdText}
                onChange={(e) => setCmdText(e.target.value)}
                onBlur={() => saveChat({ commands: cmdText.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="!wx, !town, !shoutout"
                className="w-full rounded bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-500"
              />
            </div>
            <Toggle testId="chat-includename-toggle" checked={!!chatCfg.include_name} onChange={(v) => saveChat({ include_name: v })} label="Mention the viewer's name" />
          </div>

          {/* On-screen command tip */}
          <div className="mt-5 border-t border-white/10 pt-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-oswald text-xs font-bold uppercase tracking-widest text-gray-300">On-screen command tip</div>
                <p className="mt-1 text-xs text-gray-500">Periodically shows a reminder on the broadcast so viewers learn the command.</p>
              </div>
              <Toggle testId="tip-enabled-toggle" checked={!!chatCfg.tip_enabled} onChange={(v) => saveChat({ tip_enabled: v })} label={chatCfg.tip_enabled ? "On" : "Off"} />
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <Label>Tips (one per line — they rotate in order)</Label>
                <textarea
                  data-testid="tip-text-input"
                  value={tipText}
                  onChange={(e) => setTipText(e.target.value)}
                  onBlur={() => saveChat({ tips: tipText.split("\n").map((s) => s.trim()).filter(Boolean) })}
                  rows={3}
                  placeholder={"Type !wx <your town> in chat for your local radar!\nTap Subscribe for 24/7 Georgia weather.\nHave two ways to get severe warnings."}
                  className="w-full resize-y rounded bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <Label>Every</Label>
                <select
                  data-testid="tip-interval-select"
                  value={chatCfg.tip_interval_min || 5}
                  onChange={(e) => saveChat({ tip_interval_min: Number(e.target.value) })}
                  className="rounded bg-white/10 px-3 py-2 text-sm text-white"
                >
                  <option value={3}>3 min</option>
                  <option value={5}>5 min</option>
                  <option value={10}>10 min</option>
                  <option value={15}>15 min</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <Label>Severe-weather tips (auto-shown while a warning is active)</Label>
              <textarea
                data-testid="tip-severe-input"
                value={severeText}
                onChange={(e) => setSevereText(e.target.value)}
                onBlur={() => saveChat({ tips_severe: severeText.split("\n").map((s) => s.trim()).filter(Boolean) })}
                rows={3}
                placeholder={"Warning in effect — get to an interior room on the lowest floor NOW.\nTake every warning seriously."}
                className="w-full resize-y rounded bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-500"
              />
              {chatStatus.threat_level && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-400" data-testid="threat-level-indicator">
                  Current state:
                  <span className="rounded px-2 py-0.5 font-oswald font-bold uppercase tracking-wide text-[#0a0f1c]" style={{ background: chatStatus.threat_color || "#2a9d3a" }}>{chatStatus.threat_level}</span>
                  <span className="text-gray-500">— {chatStatus.threat_level === "Calm" || chatStatus.threat_level === "Watching" ? "showing general tips" : "showing severe-weather tips"}</span>
                </div>
              )}
            </div>
          </div>
          <div data-testid="chat-status" className="mt-4 flex items-center gap-3 rounded-sm border border-white/10 bg-black/40 px-4 py-3 text-sm text-gray-300">
            {!chatStatus.enabled ? (
              <span>Turned off. Flip it on to start reading chat.</span>
            ) : (
              <>
                <span className={chatStatus.connected ? "live-dot" : "h-2 w-2 rounded-full bg-gray-600"} />
                <span>
                  {chatStatus.connected ? "Connected to live chat" : `Waiting — ${chatStatus.note || "…"}`}
                  {chatStatus.last_fired_place ? ` · last shoutout: ${chatStatus.last_fired_place}` : ""}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="flex items-center gap-2">
          <Tv className="h-5 w-5 text-[#1f63c9]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Broadcast Cut-In</div>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          The moment a new NWS warning is detected (and pushed to Facebook / X), the same polygon graphic interrupts the
          on-air loop so TV and social stay perfectly in sync. Fires even when a social post fails — TV never misses a warning.
        </p>

        <div className="mt-5 grid gap-5 md:grid-cols-3">
          <Toggle testId="cutin-enabled-toggle" checked={cfg.enabled} onChange={(v) => save({ enabled: v })} label="Cut-in enabled" />
          <Toggle testId="cutin-spc-toggle" checked={cfg.include_spc} onChange={(v) => save({ include_spc: v })} label="Include SPC outlook updates" />
          <div>
            <Label>On-air dwell: <span className="text-white" data-testid="cutin-dwell-value">{cfg.dwell_s}s</span></Label>
            <input
              data-testid="cutin-dwell-slider"
              type="range" min={15} max={180} step={5}
              value={cfg.dwell_s}
              onChange={(e) => setCfg({ ...cfg, dwell_s: Number(e.target.value) })}
              onMouseUp={(e) => save({ dwell_s: Number(e.target.value) })}
              onTouchEnd={(e) => save({ dwell_s: Number(e.target.value) })}
              className="w-full accent-[#1f63c9]"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            data-testid="cutin-rehearse-btn"
            onClick={rehearse}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-sm bg-white px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-[#0a0f1c] hover:bg-gray-200 disabled:opacity-60"
          >
            <Eye className="h-4 w-4" /> Rehearse in Browser (safe)
          </button>
          <button
            data-testid="cutin-fire-test-btn"
            onClick={fireTest}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-sm border border-[#1f63c9] px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-[#ff6b7a] hover:bg-[#1f63c9]/20 disabled:opacity-60"
          >
            <Zap className="h-4 w-4" /> Fire Test ON AIR
          </button>
          <button
            data-testid="cutin-end-btn"
            onClick={endNow}
            disabled={!active}
            className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] disabled:opacity-40"
          >
            <Square className="h-4 w-4" /> Pull Off Air Now
          </button>
          <a
            href="/broadcast" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-widest text-gray-300 hover:text-white"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open broadcast preview
          </a>
        </div>

        <div
          data-testid="cutin-onair-status"
          className={`mt-5 flex items-center gap-3 rounded-sm border px-4 py-3 ${active ? "border-[#1f63c9] bg-[#1f63c9]/10" : "border-white/10 bg-black/40"}`}
        >
          <span className={active ? "live-dot" : "h-2 w-2 rounded-full bg-gray-600"} />
          <div className="text-sm text-white">
            {active
              ? <>ON AIR NOW — <span className="font-bold">{active.title}</span> · {active.remaining_s}s remaining</>
              : "No cut-in on air. Loop running normally."}
          </div>
        </div>
      </div>

      <div className="glass rounded-sm p-6">
        <div className="mb-3 font-oswald text-sm font-bold uppercase tracking-widest text-white">Cut-In History</div>
        {history.length === 0 ? (
          <div className="text-sm text-gray-500" data-testid="cutin-history-empty">No cut-ins yet. Fire a test to see one here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="cutin-history-table">
              <thead className="font-oswald text-[10px] uppercase tracking-widest text-gray-500">
                <tr><th className="pb-2">When</th><th className="pb-2">Type</th><th className="pb-2">Title</th><th className="pb-2">Area</th><th className="pb-2">Social</th><th className="pb-2">Image</th></tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-white/5 text-gray-300" data-testid={`cutin-row-${h.id}`}>
                    <td className="py-2 font-mono text-xs">{h.created_at ? new Date(h.created_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "—"}</td>
                    <td className="py-2 font-mono text-xs uppercase">{h.kind}</td>
                    <td className="py-2 text-white">{h.title}</td>
                    <td className="py-2 max-w-[260px] truncate text-xs">{h.area}</td>
                    <td className="py-2 space-x-3">
                      <PlatformBadge icon={Facebook} status={h.platforms?.facebook} />
                      <PlatformBadge icon={Twitter} status={h.platforms?.twitter} />
                    </td>
                    <td className="py-2 space-x-2">
                      {h.image_url ? <a className="text-[#1f63c9] hover:underline" href={h.image_url} target="_blank" rel="noreferrer">16:9</a> : "—"}
                      {h.portrait_url && <a className="text-[#1f63c9] hover:underline" href={h.portrait_url} target="_blank" rel="noreferrer">portrait</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
